import { atom, useAtom } from 'jotai';
import { pushNotificationAtom } from '../features/notifications/notification-store';

export interface UiState {
  sidebarOpen: boolean;
  activeModal: string | null;
  sidebarCollapsed: boolean;
}

export interface ToastInput {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

const initialUiState: UiState = {
  sidebarOpen: true,
  activeModal: null,
  sidebarCollapsed: false,
};

const uiStateAtom = atom<UiState>(initialUiState);

/**
 * Browser-owned UI state.
 *
 * Toast feedback is deliberately delegated to the canonical notification
 * queue rendered by GlobalNotifications. Keeping one queue prevents successful
 * mutations from writing to an invisible parallel store.
 */
export function useUiStore() {
  const [state, setState] = useAtom(uiStateAtom);
  const [, pushNotification] = useAtom(pushNotificationAtom);

  const toggleSidebar = () => setState(prev => ({ ...prev, sidebarOpen: !prev.sidebarOpen }));
  const setSidebarCollapsed = (collapsed: boolean) =>
    setState(prev => ({ ...prev, sidebarCollapsed: collapsed }));

  const openModal = (modal: string) => setState(prev => ({ ...prev, activeModal: modal }));
  const closeModal = () => setState(prev => ({ ...prev, activeModal: null }));

  const addToast = (toast: ToastInput) => {
    pushNotification({
      message: toast.message,
      severity: toast.type,
      duration: toast.duration ?? 5000,
    });
  };

  return {
    ...state,
    toggleSidebar,
    setSidebarCollapsed,
    openModal,
    closeModal,
    addToast,
  };
}

export const sidebarOpenAtom = atom(
  get => get(uiStateAtom).sidebarOpen,
  (get, set, open: boolean) => {
    const current = get(uiStateAtom);
    set(uiStateAtom, { ...current, sidebarOpen: open });
  },
);

export const activeModalAtom = atom(
  get => get(uiStateAtom).activeModal,
  (get, set, modal: string | null) => {
    const current = get(uiStateAtom);
    set(uiStateAtom, { ...current, activeModal: modal });
  },
);
